import classNames from "classnames";
import { Text } from "@jsq/ds/core";
import { Icon, type IconType, type IconVariant, type IconSize } from "@jsq/ds/icons";
import { ReportTemplateVersionStatus } from "~/graphql/supergraphEnums";
import * as styles from "./styles_vanilla.css";
import type { ReportTemplateVersionStatus as ReportTemplateVersionStatusType } from "~/graphql/supergraphTypes";

export type TemplateStatus = "approved" | "active" | "pending" | "denied" | "archived";

type StatusConfig = {
  icon: IconType;
  iconVariant: IconVariant;
  className: string;
  label: string;
};

const STATUS_CONFIG: Record<TemplateStatus, StatusConfig> = {
  approved: {
    icon: "circle-check",
    iconVariant: "solid",
    className: styles.success,
    label: "Approved for use",
  },
  active: {
    icon: "circle-check",
    iconVariant: "solid",
    className: styles.success,
    label: "Active",
  },
  pending: {
    icon: "stamp",
    iconVariant: "outline",
    className: styles.neutral,
    label: "Waiting for client authorization",
  },
  denied: {
    icon: "xmark-circle",
    iconVariant: "solid",
    className: styles.danger,
    label: "Template denied",
  },
  archived: {
    icon: "box-archive",
    iconVariant: "solid",
    className: styles.muted,
    label: "Archived",
  },
};

export const TEMPLATE_STATUS_LABEL = Object.fromEntries(
  (Object.entries(STATUS_CONFIG) as [TemplateStatus, StatusConfig][]).map(
    ([status, config]) => [status, config.label],
  ),
) as Record<TemplateStatus, string>;

/**
 * Normalize a `ReportTemplateVersionStatus` (+ optional `isArchived`) into the
 * canonical `TemplateStatus` used by the shared badge.
 */
export const fromVersionStatus = (
  versionStatus: ReportTemplateVersionStatusType,
  isArchived?: boolean,
): TemplateStatus => {
  if (isArchived) return "archived";
  switch (versionStatus) {
    case ReportTemplateVersionStatus.Approved:
      return "approved";
    case ReportTemplateVersionStatus.Denied:
      return "denied";
    case ReportTemplateVersionStatus.Pending:
    default:
      return "pending";
  }
};

export type TemplateStatusBadgeProps = {
  status: TemplateStatus;
  /**
   * Icon size; `Text` variant stays at `regular-md` to match Figma in both cases.
   * @default "medium"
   */
  iconSize?: IconSize;
  className?: string;
};

export const TemplateStatusBadge = ({
  status,
  iconSize = "medium",
  className,
}: TemplateStatusBadgeProps) => {
  const config = STATUS_CONFIG[status];

  return (
    <span className={classNames(styles.badge, config.className, className)}>
      <Icon type={config.icon} variant={config.iconVariant} size={iconSize} />
      <Text variant="regular-md" asSpan>
        {config.label}
      </Text>
    </span>
  );
};
